import React, { useState } from 'react';
import { Box, Alert, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ERROR_MESSAGES } from '../constants';

interface ErrorScreenProps {
  error: string;
  onRetry: () => void;
  onPermissionRequest?: () => Promise<void>;
}

export const ErrorScreen: React.FC<ErrorScreenProps> = ({
  error,
  onRetry,
  onPermissionRequest
}) => {
  const { t } = useTranslation();
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);

  // Check if the error is related to media permissions
  const isPermissionError = error.includes('permissions') ||
                           error.includes('Camera and microphone') ||
                           error.includes('denied') ||
                           error.includes('negadas') ||
                           error.includes('camera/microphone icon') ||
                           error === ERROR_MESSAGES.MEDIA_PERMISSIONS_DENIED ||
                           error === ERROR_MESSAGES.MEDIA_DEVICES_NOT_AVAILABLE ||
                           error === ERROR_MESSAGES.NO_DEVICES_FOUND;

  // Check if it's a specific "denied" error that requires manual browser settings change
  const isDeniedError = error.includes('denied') ||
                       error.includes('negadas') ||
                       error.includes('camera/microphone icon') ||
                       error.includes('address bar');

  const handleRequestPermissions = async () => {
    if (!onPermissionRequest) return;

    setIsRequestingPermissions(true);
    try {
      await onPermissionRequest();
    } catch (err) {
      console.error('Error requesting permissions:', err);
    } finally {
      setIsRequestingPermissions(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh" p={2}>
      <Alert severity="error" sx={{ maxWidth: 600 }}>
        <Typography variant="h6" gutterBottom>
          {t('Failed to connect to room')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t(error)}
        </Typography>

        {isPermissionError && (
          <Box mb={2}>
            {isDeniedError ? (
              <Box>
                <Typography variant="body2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>
                  {t('Permissions have been permanently denied by your browser.')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('To fix this, you need to manually change your browser settings:')}
                </Typography>
                <Box component="ol" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    {t('Click the camera/microphone icon in your browser\'s address bar')}
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    {t('Select "Allow" for camera and microphone permissions')}
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    {t('Refresh this page')}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('This error is usually caused by missing camera or microphone permissions.')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleRequestPermissions}
                  disabled={isRequestingPermissions}
                  sx={{ mr: 1 }}
                >
                  {isRequestingPermissions ? t('Requesting...') : t('Grant Camera & Microphone Access')}
                </Button>
              </Box>
            )}
          </Box>
        )}

        <Box>
          <Button
            variant={isPermissionError ? "outlined" : "contained"}
            onClick={onRetry}
            disabled={isRequestingPermissions}
          >
            {t('Try Again')}
          </Button>
        </Box>
      </Alert>
    </Box>
  );
};
